'use client';

import { useMemo } from 'react';
import { ArrowUpDownIcon, Tick01Icon } from '@terra/ui-icons';
import { FieldWrapper } from '../../../components/field-wrapper/FieldWrapper';
import { Select, createSelectListCollection } from '../../../components/select';

type InternalNetworkCredentialSelectFieldProps<TValue extends string> = {
  label: string;
  helperText?: string;
  value: TValue;
  items: Array<{ label: string; value: TValue }>;
  isValue: (value: string) => value is TValue;
  onValueChange: (value: TValue) => void;
};

export function InternalNetworkCredentialSelectField<TValue extends string>({
  label,
  helperText,
  value,
  items,
  isValue,
  onValueChange,
}: InternalNetworkCredentialSelectFieldProps<TValue>) {
  const collection = useMemo(
    () => createSelectListCollection({ items }),
    [items],
  );

  return (
    <FieldWrapper label={label} helperText={helperText}>
      <Select.Root
        size="sm"
        positioning={{ sameWidth: true }}
        width="full"
        collection={collection}
        value={[value]}
        onValueChange={(details) => {
          const next = details.value[0];

          if (next && isValue(next)) {
            onValueChange(next);
          }
        }}
      >
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText />
            <ArrowUpDownIcon size={14} />
          </Select.Trigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content>
            <Select.ItemGroup>
              {collection.items.map((item) => (
                <Select.Item key={item.value} item={item}>
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <Tick01Icon size={14} />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.ItemGroup>
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
    </FieldWrapper>
  );
}
